<template>
  <!-- eslint-disable vue/no-mutating-props -->
  <el-cascader
    popper-class="fuzzy-cascader-wrap"
    ref="cascader"
    v-model="model[prop]"
    v-bind="innerAttrs"
    :placeholder="innerAttrs.placeholder || '请选择'"
    :props="innerProps"
    :show-all-levels="showAllLevels"
    :disabled="disabled"
    :clearable="clearable"
    :filterable="filterable"
    :options="options"
    @visible-change="handleVisibleChange"
  />
</template>

<script lang="ts">
export default { inheritAttrs: false }
</script>

<script setup lang="ts">
import { _, tpl, ref, watch, useCurrentAppInstance } from '@zto/zpage'
import type { ApiRequestAction } from '@zto/zpage'
import { useFormItem } from '../util'

const props = withDefaults(
  defineProps<{
    model: Record<string, any>
    prop: string
    labelProp?: string
    extraProp?: Array<any>
    innerProps?: Record<string, any>
    showAllLevels?: boolean // 输入框中是否显示选中值的完整路径
    disabled?: boolean
    clearable?: boolean
    filterable?: boolean
    optionsOriginDataProp?: string // 接口返回的options的原始数据字段
    optionsFn?: Function // 自定义函数，options的处理函数

    api?: ApiRequestAction | string
    apiParams?: Record<string, any>
    pageSize?: number

    triggerFocus?: boolean // 是否简单
    remote?: boolean
  }>(),
  {
    disabled: false,
    clearable: true,
    filterable: true,

    pageSize: 40,

    triggerFocus: false,
    remote: true
  }
)

const { innerAttrs } = useFormItem(props)

const app = useCurrentAppInstance()
const apiRequest = app.request

const cascader = ref()
const options: any = ref([])

// 当不是远程搜索时候，只有在第一次 focus 时候获取数据，其它时候不加载数据 例如只有几个枚举
const firstNoRemote = ref(true)

// 下拉框出现时触发
const handleVisibleChange = (value: boolean) => {
  if (value) {
    // 远程搜索 如果没有options 则请求
    if (!props.triggerFocus) return

    if (props.remote) {
      getOptions()
    } else if (firstNoRemote.value) {
      // 非远程搜索只请求一次
      getOptions()
      firstNoRemote.value = false
    }
  }
}

const getOptions = async () => {
  const context = app.useContext()
  const params = tpl.deepFilter(props.apiParams, context)

  if (apiRequest && props.api) {
    const pageSize = props.pageSize

    const queryAction: any = typeof props.api === 'string' ? { url: props.api } : { ...props.api }
    queryAction.type = 'fuzzy-cascader'

    const postData = _.shallowTrim({ ...params })

    const res = await apiRequest({
      action: queryAction,
      params: postData,
      pageIndex: 1,
      pageSize
    })

    if (!res) {
      options.value = []
    } else if (res && props.optionsFn) {
      // 如果有options的处理函数，走该自定义函数
      options.value = props.optionsFn(res)
      props.optionsOriginDataProp && (props.model[props.optionsOriginDataProp] = res) // 植入props.optionsOriginDataProp的值
    } else if (Array.isArray(res)) {
      options.value = res
    } else {
      options.value = []
    }
  } else {
    options.value = []
  }
}

// 如果有options的处理函数，走该自定义函数，且先请求一次，只请求一次
if (props.optionsFn) {
  // 非远程搜索只请求一次
  getOptions()
  firstNoRemote.value = false
}

watch(
  () => [props.api, props.apiParams],
  ([curApi, curApiParams], [oldApi, oldApiParams]) => {
    if (_.deepEqual(curApi, oldApi) && _.deepEqual(curApiParams, oldApiParams)) return

    firstNoRemote.value = true
    options.value = []
  }
)

watch(
  () => props.model[props.prop],
  (cur, old) => {
    // console.log('cur===', cur)
    // console.log('ref===', cascader.value.getCheckedNodes()?.[0]?.data)
    // 如果配置了labelProp，则把当前选择的label设置为labelProp的值
    if (props.labelProp) {
      props.model[props.labelProp] = cascader.value.inputValue
    }

    if (props?.extraProp && Array.isArray(props?.extraProp) && props?.extraProp?.length) {
      const data = cascader.value.getCheckedNodes()?.[0]?.data
      props?.extraProp?.forEach(item => {
        props.model[item] = data?.[item] || undefined
      })
    }

    // 当点击重置时，处理特殊情况[undefined]
    if (cur?.[0] === undefined) {
      props.model[props.prop] = undefined
    }
  }
)
</script>

<style lang="scss">
.fuzzy-cascader-wrap {
  .el-cascader-node__label {
    max-width: 200px !important;
  }
}
</style>
